// Кабинет закупщика — перенос раздела «📋 Кабинет закупщика» с портала 55.
//
// ЧТО НА ПОРТАЛЕ И ЧТО ИЗ ЭТОГО ЗДЕСЬ.
// На портале раздел состоит из трёх слоёв:
//   1. Заявка ЗКП (отдел, статья, склад, сделка, позиции, срок) — лежит в KV самого портала
//      (`zkp:all` в Cloudflare), в Bitrix и в 1С её нет вовсе. Ядро её не видит и видеть не
//      может: это данные портала, а не CRM.
//   2. Счёт по заявке: распознавание PDF → элемент списка 18 → бизнес-процесс Bitrix рождает
//      карточку смарт-процесса 1160 «Счета на оплату 2.0». Вот ЭТО ядро отдаёт, и на этом
//      построен экран: стадии согласования, плановая дата платежа, факт оплаты, суммы, отдел,
//      статья расходов, поставщик и сделка.
//   3. Выгрузка счёта в 1С → заказ поставщику (Document_ЗаказПоставщику). Тоже доступно —
//      вторая вкладка: что заказано, что подтверждено, что уже поступило на склад.
// Не перенесено намеренно: распознавание PDF, отправка на согласование, выгрузка в 1С и
// закрытие заданий бизнес-процессов — это ЗАПИСЬ. Она идёт отдельным шагом и с согласия
// владельца процесса; см. карточку «Чего здесь нет» внизу экрана.
//
// ЭКРАН ТОЛЬКО ЧИТАЕТ. Правка карточки 1160 перезапускает бизнес-процессы Bitrix («изменеие
// счета», «Предварительный счет»), а те двигают стадии и раздают задания живым людям.
import { useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { useCompanyTitles, useEntityTitle, useFields, useRefs } from '../api/hooks'
import {
  usePayCount, usePayRequests, useSupplierOrders, useUtDicts, useUtNamesForPage,
} from '../api/purchasing'
import type { ScopeMark } from '../api/purchasing'
import type { ListParams } from '../api/types'
import { EntityLink } from '../components/EntityLink'
import { EntityPicker } from '../components/EntityPicker'
import { FilterBar } from '../components/FilterBar'
import { OdataFreshness, OdataState } from '../components/OdataState'
import { QueryState } from '../components/QueryState'
import type { EntityRef } from '../lib/entities'
import { fmtDate, fmtDays, fmtMoney, fmtText, plural, pluralN, todayIso } from '../lib/format'
import {
  PAY_PHASES, PUR, PUR_ENTITY, PUR_LIST_SELECT, PUR_TYPE_ID, WINDOWS, bitrixPayUrl,
  emptyUtNames, overdueFilter, payRequestView, sumRows, supplierStatusRu, toSupplyRow,
  totalsByCurrency, unknownStages,
} from '../lib/purchasing'
import type { PayPhaseKey, PayRequestView, SupplyRow } from '../lib/purchasing'

const PAGE = 50            // страница ядра (PAGE в core_api) — своей задать нельзя
const SUPPLY_PAGE = 50     // строк заказов поставщику на страницу (листаем локально)

type Tab = 'requests' | 'supply'

export default function Purchasing() {
  const [tab, setTab] = useState<Tab>('requests')

  return (
    <>
      <div className="page-head">
        <h1>Кабинет закупщика</h1>
        <div className="spacer" />
        <span className="muted">только чтение</span>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="hint">
          Заявки на оплату поставщикам — смарт-процесс {PUR_TYPE_ID} «Счета на оплату 2.0»
          из ядра. Карточку рождает бизнес-процесс списка 18, портал 55 дописывает компанию
          и сделку. Вторая вкладка — заказы поставщику в 1С УТ: что заказано и что поступило.
        </div>
        <div className="filters" style={{ marginTop: 12 }}>
          <button type="button" onClick={() => setTab('requests')}
                  disabled={tab === 'requests'}>
            Заявки на оплату
          </button>
          <button type="button" onClick={() => setTab('supply')} disabled={tab === 'supply'}>
            Заказы поставщику в 1С
          </button>
        </div>
      </div>

      {tab === 'requests' ? <PayRequests /> : <SupplierOrders />}

      <NotHere />
    </>
  )
}

// ═══════════════════════════ вкладка 1: заявки на оплату (1160) ═══════════════════════════

type SortKey = 'plan' | 'planDesc' | 'updated' | 'id'

const SORTS: Array<{ key: SortKey; label: string; order: Record<string, 'ASC' | 'DESC'> }> = [
  { key: 'plan', label: 'по плановому платежу ↑ (сначала горит)', order: { [PUR.planDate]: 'ASC' } },
  { key: 'planDesc', label: 'по плановому платежу ↓', order: { [PUR.planDate]: 'DESC' } },
  { key: 'updated', label: 'по изменению', order: { updatedTime: 'DESC' } },
  { key: 'id', label: 'по номеру карточки', order: { id: 'DESC' } },
]

function PayRequests() {
  const [sp, setSp] = useSearchParams()
  // Компания и сделка живут в адресе: ссылку «что мы должны этому поставщику» пересылают.
  const companyId = sp.get('company') || ''
  const dealId = sp.get('deal') || ''

  const [phase, setPhase] = useState<PayPhaseKey | ''>('')
  const [stage, setStage] = useState('')
  const [invoiceNo, setInvoiceNo] = useState('')
  const [text, setText] = useState('')
  const [dept, setDept] = useState('')
  const [expense, setExpense] = useState('')
  const [payer, setPayer] = useState('')
  const [assigned, setAssigned] = useState('')
  const [planFrom, setPlanFrom] = useState('')
  const [planTo, setPlanTo] = useState('')
  const [onlyOverdue, setOnlyOverdue] = useState(false)
  const [sort, setSort] = useState<SortKey>('plan')
  const [start, setStart] = useState(0)

  const refs = useRefs()
  const f = useFields(PUR_ENTITY)
  const today = todayIso()

  const stageOptions = useMemo(() => refs.itemStageOptions(PUR_TYPE_ID), [refs])
  const missing = useMemo(() => unknownStages(refs), [refs])

  const companyTitle = useEntityTitle('company', companyId || undefined)
  const companyValue: EntityRef | null = companyId
    ? { kind: 'company', id: companyId, title: companyTitle.data || `#${companyId}` }
    : null
  const dealTitle = useEntityTitle('deal', dealId || undefined)
  const dealValue: EntityRef | null = dealId
    ? { kind: 'deal', id: dealId, title: dealTitle.data || `#${dealId}` }
    : null

  const params = useMemo<ListParams>(() => {
    const filter: Record<string, unknown> = {}
    // Стадия точнее фазы: если выбрана конкретная, фазу не подмешиваем — ядро умеет только «И»,
    // и два условия по одному полю дали бы пустой список.
    if (onlyOverdue) Object.assign(filter, overdueFilter(today))
    if (stage) filter.stageId = stage
    else if (phase) filter.stageId = PAY_PHASES.find((p) => p.key === phase)?.stages ?? []
    if (companyId) filter.companyId = companyId
    if (dealId) filter.parentId2 = dealId
    if (invoiceNo.trim()) filter[`%${PUR.invoiceNo}`] = invoiceNo.trim()
    if (text.trim()) filter['%title'] = text.trim()
    if (dept) filter[PUR.dept] = dept
    if (expense) filter[PUR.expense] = expense
    if (payer) filter[PUR.payer] = payer
    if (assigned) filter.assignedById = assigned
    // Диапазон по плановой дате: поле лежит в raw ISO-строкой, и ядро сравнивает её как дату.
    // Просроченность при этом считаем не фильтром, а по значению — см. payRequestView.
    if (planFrom && !onlyOverdue) filter[`>=${PUR.planDate}`] = planFrom
    if (planTo && !onlyOverdue) filter[`<=${PUR.planDate}`] = planTo
    const order = SORTS.find((s) => s.key === sort)?.order ?? { [PUR.planDate]: 'ASC' }
    return { filter, order, select: PUR_LIST_SELECT, start }
  }, [stage, phase, onlyOverdue, today, companyId, dealId, invoiceNo, text, dept, expense,
      payer, assigned, planFrom, planTo, sort, start])

  const q = usePayRequests(params)
  const data = q.data

  const rows = useMemo(
    () => (data?.items ?? []).map((it) => payRequestView(it, refs, f.enumText, today)),
    [data, refs, f, today],
  )
  const totals = useMemo(() => totalsByCurrency(rows), [rows])
  const disputed = useMemo(() => rows.filter((r) => r.paidDisputed).length, [rows])
  const companies = useCompanyTitles(rows.map((r) => r.companyId))

  const resetAnd = <T,>(setter: (v: T) => void) => (v: T) => { setStart(0); setter(v) }

  const setQueryRef = (key: 'company' | 'deal') => (v: EntityRef | null) => {
    setStart(0)
    const next = new URLSearchParams(sp)
    if (v) next.set(key, v.id)
    else next.delete(key)
    setSp(next, { replace: true })
  }

  const deptEnum = f.meta(PUR.dept)?.enum ?? []
  const expenseEnum = f.meta(PUR.expense)?.enum ?? []
  const payerEnum = f.meta(PUR.payer)?.enum ?? []

  return (
    <>
      <div className="card" style={{ marginBottom: 16 }}>
        {/* Два блока сворачиваются порознь и ключи у них разные: искать заявку начинают
            сверху, а разрезы (отдел, статья, сортировка) человек трогает не каждый раз —
            общий ключ схлопывал бы или раскрывал их разом. */}
        <FilterBar id="purchasing-invoices">
          <div className="f" style={{ minWidth: 210 }}>
            <label htmlFor="pu-phase">Что с заявкой</label>
            <select id="pu-phase" value={phase}
                    onChange={(e) => resetAnd(setPhase)(e.target.value as PayPhaseKey | '')}>
              <option value="">любое состояние</option>
              {PAY_PHASES.map((p) => (
                <option key={p.key} value={p.key} title={p.hint}>{p.label}</option>
              ))}
            </select>
          </div>

          <div className="f" style={{ minWidth: 200 }}>
            <label htmlFor="pu-stage">Стадия</label>
            <select id="pu-stage" value={stage} onChange={(e) => resetAnd(setStage)(e.target.value)}
                    disabled={stageOptions.length === 0}>
              <option value="">любая</option>
              {stageOptions.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          </div>

          <div className="f">
            <label htmlFor="pu-inv">Номер счёта</label>
            <input id="pu-inv" value={invoiceNo}
                   onChange={(e) => resetAnd(setInvoiceNo)(e.target.value)} />
          </div>

          <div className="f" style={{ flex: 2, minWidth: 200 }}>
            <label htmlFor="pu-text">Название содержит</label>
            <input id="pu-text" value={text} placeholder="за что, поставщик, автор"
                   onChange={(e) => resetAnd(setText)(e.target.value)} />
          </div>

          <div style={{ flex: 2, minWidth: 220 }}>
            <EntityPicker kind="company" id="pu-company" label="Кому платим"
                          value={companyValue} onChange={setQueryRef('company')} />
          </div>

          <div style={{ flex: 2, minWidth: 220 }}>
            <EntityPicker kind="deal" id="pu-deal" label="Сделка"
                          value={dealValue} onChange={setQueryRef('deal')} />
          </div>
        </FilterBar>

        <FilterBar id="purchasing-invoices-breakdown" style={{ marginTop: 12 }}>
          <div className="f" style={{ minWidth: 190 }}>
            <label htmlFor="pu-dept">Для какого отдела</label>
            <select id="pu-dept" value={dept} onChange={(e) => resetAnd(setDept)(e.target.value)}
                    disabled={deptEnum.length === 0}>
              <option value="">любой</option>
              {deptEnum.map((o) => <option key={o.id} value={o.id}>{o.value}</option>)}
            </select>
          </div>

          <div className="f" style={{ minWidth: 190 }}>
            <label htmlFor="pu-exp">Статья расходов</label>
            <select id="pu-exp" value={expense} onChange={(e) => resetAnd(setExpense)(e.target.value)}
                    disabled={expenseEnum.length === 0}>
              <option value="">любая</option>
              {expenseEnum.map((o) => <option key={o.id} value={o.id}>{o.value}</option>)}
            </select>
          </div>

          <div className="f" style={{ minWidth: 170 }}>
            <label htmlFor="pu-payer">Платим от</label>
            <select id="pu-payer" value={payer} onChange={(e) => resetAnd(setPayer)(e.target.value)}
                    disabled={payerEnum.length === 0}>
              <option value="">любой компании</option>
              {payerEnum.map((o) => <option key={o.id} value={o.id}>{o.value}</option>)}
            </select>
          </div>

          <div className="f" style={{ minWidth: 190 }}>
            <label htmlFor="pu-resp">Ответственный</label>
            <select id="pu-resp" value={assigned}
                    onChange={(e) => resetAnd(setAssigned)(e.target.value)}
                    disabled={refs.users.length === 0}>
              <option value="">любой</option>
              {refs.users.map((u) => (
                <option key={u.id} value={u.id}>{u.active ? u.name : `${u.name} (уволен)`}</option>
              ))}
            </select>
          </div>

          <div className="f">
            <label htmlFor="pu-from">Плановый платёж от</label>
            <input id="pu-from" type="date" value={planFrom} disabled={onlyOverdue}
                   onChange={(e) => resetAnd(setPlanFrom)(e.target.value)} />
          </div>
          <div className="f">
            <label htmlFor="pu-to">до</label>
            <input id="pu-to" type="date" value={planTo} disabled={onlyOverdue}
                   onChange={(e) => resetAnd(setPlanTo)(e.target.value)} />
          </div>

          <div className="f" style={{ minWidth: 230 }}>
            <label htmlFor="pu-sort">Сортировка</label>
            <select id="pu-sort" value={sort}
                    onChange={(e) => resetAnd(setSort)(e.target.value as SortKey)}>
              {SORTS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
          </div>

          <div className="f" style={{ minWidth: 220 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 18 }}>
              <input type="checkbox" checked={onlyOverdue} style={{ width: 'auto' }}
                     onChange={(e) => resetAnd(setOnlyOverdue)(e.target.checked)} />
              только просроченные и незакрытые
            </label>
          </div>
        </FilterBar>

        {onlyOverdue && (
          <div className="hint" style={{ marginTop: 10 }}>
            Отобраны заявки на открытых стадиях (согласование и ожидание оплаты), у которых
            нет даты оплаты, а плановая дата платежа не позже сегодняшней — ровно то же
            условие, что считает плитка «Просрочен план платежа». Диапазон дат при этом
            выключен: ядро умеет только «И», и два условия по одной дате дали бы пустой список.
          </div>
        )}
        {missing.length > 0 && (
          <div className="notice" style={{ marginTop: 10 }}>
            В Bitrix появились стадии смарт-процесса {PUR_TYPE_ID}, которых нет в нашей
            группировке: {missing.map((s) => s.name).join(', ')}. Такие заявки не попадут ни
            в одну плитку — их видно только через фильтр «Стадия».
          </div>
        )}
        {!f.loaded && (
          <div className="hint" style={{ marginTop: 10 }}>
            Справочник полей смарт-процесса {PUR_TYPE_ID} из ядра не пришёл: отдел, статья
            расходов и признаки утверждения показаны сырыми кодами перечислений, фильтры по
            ним отключены.
          </div>
        )}
        {!refs.hasItemStages(PUR_TYPE_ID) && (
          <div className="hint" style={{ marginTop: 10 }}>
            Стадии смарт-процесса {PUR_TYPE_ID} из ядра не пришли: в таблице они показаны
            сырыми кодами Bitrix, фильтр по стадии отключён. Плитки по фазам работают —
            они строятся по кодам стадий, а не по названиям.
          </div>
        )}
      </div>

      <PhaseTiles />

      <QueryState isLoading={q.isLoading} error={q.error} stale={data?.stale} ageMin={data?.ageMin}>
        <ScopeNotice scope={data?.scope} />

        {totals.length > 0 && (
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="page-head" style={{ marginBottom: 10 }}>
              <h2 style={{ margin: 0 }}>Суммы по странице</h2>
              <div className="spacer" />
              <span className="hint">
                по показанным {rows.length}{' '}
                {plural(rows.length, 'строке', 'строкам', 'строкам')} из {data?.total ?? 0}
              </span>
            </div>
            <div className="tiles">
              {totals.map((t) => (
                <div key={t.currency} className="tile">
                  <div className="t-label">Сумма счетов, {t.currency}</div>
                  <div className="t-sum">{fmtMoney(t.sum, t.currency)}</div>
                  <div className="t-label">{pluralN(t.count, 'заявка', 'заявки', 'заявок')}</div>
                </div>
              ))}
            </div>
            <div className="hint" style={{ marginTop: 10 }}>
              Это сумма ЗАГРУЖЕННОЙ страницы
              ({pluralN(rows.length, 'запись', 'записи', 'записей')}), а не всей выборки:
              агрегатов ядро не считает, оно отдаёт данные страницами по {PAGE}. Валюты
              не смешиваем — в 1160 есть карточки не только в рублях.
              {disputed > 0 && ` Оплаченными считаем только те, у которых есть дата оплаты: `
                + `на этой странице у ${disputed} ${plural(disputed, 'заявки', 'заявок', 'заявок')}`
                + ` стоит флаг «Оплачен», но даты нет `
                + `и стадия открыта — такие помечены в таблице.`}
            </div>
          </div>
        )}

        <div className="card">
          <RequestsTable stale={data?.stale} rows={rows} companies={companies.data} />

          {data && data.total > PAGE && (
            <div className="pager">
              <button disabled={start === 0 || q.isFetching}
                      onClick={() => setStart(Math.max(0, start - PAGE))}>
                ← назад
              </button>
              <span className="muted">
                {start + 1}–{Math.min(start + PAGE, data.total)} из {data.total}
              </span>
              <button disabled={data.next === undefined || q.isFetching}
                      onClick={() => setStart(data.next!)}>
                вперёд →
              </button>
            </div>
          )}
        </div>
      </QueryState>
    </>
  )
}

/**
 * Пометка «показано не всё». Обязательна: ограниченная роль видит только заявки, где она
 * системный ответственный, и пустая или короткая таблица без этой подписи читается как
 * «заявок нет», то есть как «мы никому не должны».
 */
function ScopeNotice({ scope }: { scope?: ScopeMark }) {
  if (!scope?.limited) return null
  return (
    <div className="notice" style={{ marginBottom: 16 }}>
      <b>Выборка сужена по вашей зоне ответственности.</b> Показаны не все заявки компании,
      а только те, что относятся к вам.
      {scope.partial && (
        <div style={{ marginTop: 6 }}>
          {scope.note || 'Ядро отметило выборку как заведомо неполную сверх сужения по зоне.'}
        </div>
      )}
    </div>
  )
}

/**
 * Одна плитка-счётчик. Отдельным компонентом, а не хуком в цикле: хук в `map` работает
 * только пока длина массива неизменна, и это ровно та мина, которая взрывается при
 * добавлении шестой фазы.
 *
 * Считаем запросом за `total`, а не по загруженной странице: агрегатов ядро не отдаёт,
 * а «сколько ждёт согласования» по первым пятидесяти строкам — не число, а случайность.
 */
function CountTile({ label, note, filter, hot, hint }: {
  label: string
  /**
   * Подпись под числом. Тремя формами — если она склоняется вместе с ним: у плиток
   * конвейера под числом стоит «заявок», и на стадии с одной карточкой читалось
   * «1 заявок». Одной строкой — если не склоняется («из незакрытых»).
   */
  note: string | [string, string, string]
  filter: Record<string, unknown>
  hot?: boolean
  hint?: string
}) {
  const q = usePayCount(filter)
  const n = q.data?.total ?? 0
  return (
    <div className={`tile${hot && n ? ' hot' : ''}`} title={hint}>
      <div className="t-label">{label}</div>
      <div className="t-sum">{q.isLoading ? '…' : q.error ? '—' : n}</div>
      <div className="t-label">{typeof note === 'string' ? note : plural(n, ...note)}</div>
    </div>
  )
}

function PhaseTiles() {
  const today = todayIso()
  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div className="page-head" style={{ marginBottom: 10 }}>
        <h2 style={{ margin: 0 }}>Конвейер заявок</h2>
        <div className="spacer" />
        <span className="hint">по всем заявкам, не по странице</span>
      </div>
      <div className="tiles">
        {PAY_PHASES.map((p) => (
          <CountTile key={p.key} label={p.label} note={['заявка', 'заявки', 'заявок']}
                     hint={p.hint} hot={p.hot}
                     filter={{ stageId: p.stages }} />
        ))}
        <CountTile
          label="Просрочен план платежа" note="из незакрытых" hot
          hint="Открытая стадия, даты оплаты нет, плановая дата платежа уже прошла."
          filter={overdueFilter(today)}
        />
      </div>
      <div className="hint" style={{ marginTop: 10 }}>
        Прочерк вместо числа означает отказ ядра, а не ноль. Сумм в плитках нет намеренно:
        агрегатов ядро не считает, оно отдаёт данные страницами по {PAGE}, и «сумма первых
        пятидесяти» рядом со счётчиком по всем заявкам читалась бы как сумма по всем.
      </div>
    </div>
  )
}

function RequestsTable({ rows, companies, stale }: {
  rows: PayRequestView[]
  companies?: Record<string, string>
  /** Ядро предупредило, что данные устарели. Тогда строк не бывает по определению,
      и «ничего не найдено под эти фильтры» было бы неправдой: фильтры тут не при чём. */
  stale?: boolean
}) {
  const refs = useRefs()
  if (rows.length === 0) {
    return (
      <div className="empty">
        <div className="empty-title">
          {stale ? 'Список не показан: данные устарели' : 'Ничего не найдено под эти фильтры'}
        </div>
        <div className="empty-hint">
          {stale
            ? 'Ядро предупредило, что зеркало отстало от Bitrix. Пустой список означал бы «заявок нет», а это не так — мы просто не знаем.'
            : 'Снимите условия или расширьте диапазон плановых дат.'}
        </div>
      </div>
    )
  }
  return (
    /* `cards` — на телефоне таблица разбирается на карточки (styles.css, §12).
       Пятнадцать колонок не читаются вбок ни на одном телефоне. */
    <div className="table-wrap cards">
      <table>
        <thead>
          <tr>
            <th className="nowrap">№ счёта</th>
            <th className="nowrap">Дата счёта</th>
            <th>За что</th>
            <th className="num">Сумма</th>
            <th className="num">НДС</th>
            <th className="nowrap">Плановый платёж</th>
            <th className="nowrap">Оплачен</th>
            <th className="nowrap">Стадия</th>
            <th className="nowrap">Утверждён</th>
            <th>Отдел</th>
            <th>Статья</th>
            <th>Кому платим</th>
            <th className="nowrap">Сделка</th>
            <th className="nowrap">Ответственный</th>
            <th className="nowrap">В Bitrix</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id}>
              <td className="mono nowrap" data-label="№ счёта">{r.invoiceNo || <span className="muted">—</span>}</td>
              <td className="nowrap" data-label="Дата счёта">{r.invoiceDate ? fmtDate(r.invoiceDate) : <span className="muted">—</span>}</td>
              <td data-label="За что" title={r.comment || undefined}>{fmtText(r.purpose || r.title)}</td>
              <td className="num" data-label="Сумма">{r.sum === null ? '—' : fmtMoney(r.sum, r.currency)}</td>
              <td className="num" data-label="НДС">{r.vat ? fmtMoney(r.vat, r.currency) : <span className="muted">—</span>}</td>
              <td className="nowrap" data-label="Плановый платёж">
                {r.planDay ? fmtDate(r.planDay) : <span className="muted">—</span>}
                {r.overdue && r.planOverdueDays !== null && (
                  <div>
                    <span className="badge lose">{fmtDays(r.planOverdueDays)} назад</span>
                  </div>
                )}
              </td>
              <td className="nowrap" data-label="Оплачен">
                {r.paid
                  ? <span className="badge won">{fmtDate(r.payDay)}</span>
                  : <span className="muted">нет</span>}
                {r.paidDisputed && (
                  <div>
                    <span className="badge lose"
                          title="В Bitrix стоит флаг «Оплачен: Да», но даты оплаты нет, а заявка ещё на открытой стадии. Оплаченной мы её не считаем.">
                      флаг «оплачен» без даты
                    </span>
                  </div>
                )}
              </td>
              <td className="nowrap" data-label="Стадия">
                <span className={`badge ${refs.itemStageKind(r.stageId)}`}>{r.stageName}</span>
              </td>
              <td className="nowrap" data-label="Утверждён">
                {r.approved ? fmtText(r.approved) : <span className="muted">—</span>}
                {r.approvedStage && (
                  <div className="hint">{r.approvedStage}</div>
                )}
              </td>
              <td data-label="Отдел">{fmtText(r.dept)}</td>
              <td data-label="Статья" title={r.expenseExt || undefined}>{fmtText(r.expense)}</td>
              <td data-label="Кому платим">
                <EntityLink kind="company" id={r.companyId}
                            title={r.companyId ? companies?.[r.companyId] : null} />
              </td>
              <td className="nowrap" data-label="Сделка">
                <EntityLink kind="deal" id={r.dealId} />
                {r.dealFromField && (
                  <div className="hint" title="Системный родитель карточки пуст — сделка взята из поля-привязки «Сделки»">
                    из поля
                  </div>
                )}
              </td>
              <td className="nowrap" data-label="Ответственный">{refs.userName(r.assignedById)}</td>
              <td className="nowrap" data-label="В Bitrix">
                <a href={r.listUrl || bitrixPayUrl(r.id)} target="_blank" rel="noreferrer">
                  {r.listUrl ? 'счёт' : 'карточка'} ↗
                </a>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ═══════════════════════ вкладка 2: заказы поставщику в 1С УТ ═══════════════════════

function SupplierOrders() {
  const [days, setDays] = useState<number>(90)
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('')
  const [onlyOpen, setOnlyOpen] = useState(false)
  const [page, setPage] = useState(0)

  const docs = useSupplierOrders(days)
  const dicts = useUtDicts()

  const all = useMemo<SupplyRow[]>(() => {
    // Справочники могли не прийти — тогда названия пустые, но строки показать всё равно надо.
    const names = dicts.data?.names ?? emptyUtNames()
    return (docs.data?.rows ?? []).map((d) => toSupplyRow(d, names))
  }, [docs.data, dicts.data])

  const statuses = useMemo(
    () => [...new Set(all.map((r) => r.statusKey).filter(Boolean))].sort(),
    [all],
  )

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase()
    return all.filter((r) => {
      if (status && r.statusKey !== status) return false
      if (onlyOpen && r.arrivedDay) return false
      if (!s) return true
      return r.number.toLowerCase().includes(s)
        || r.supplierInvoiceNo.toLowerCase().includes(s)
        || r.dealId.includes(s)
        || r.comment.toLowerCase().includes(s)
    })
  }, [all, search, status, onlyOpen])

  const shown = filtered.slice(page * SUPPLY_PAGE, page * SUPPLY_PAGE + SUPPLY_PAGE)
  // Названия поставщиков — только для показанной страницы: партнёров в 1С 6900.
  const pageNames = useUtNamesForPage([], shown.map((r) => r.supplierRef))
  const totals = useMemo(() => sumRows(filtered), [filtered])
  const arrived = useMemo(() => sumRows(filtered.filter((r) => r.arrivedDay)), [filtered])

  const resetAnd = <T,>(setter: (v: T) => void) => (v: T) => { setPage(0); setter(v) }

  return (
    <>
      <div className="card" style={{ marginBottom: 16 }}>
        {/* Свой ключ у вкладки заказов: вкладки живут на одном экране, и раскрытые
            фильтры заявок не должны раскрывать эти. */}
        <FilterBar id="purchasing-supply">
          <div className="f" style={{ minWidth: 170 }}>
            <label htmlFor="so-days">Окно</label>
            <select id="so-days" value={days}
                    onChange={(e) => resetAnd(setDays)(Number(e.target.value))}>
              {WINDOWS.map((w) => <option key={w} value={w}>последние {fmtDays(w)}</option>)}
            </select>
          </div>
          <div className="f" style={{ flex: 2, minWidth: 240 }}>
            <label htmlFor="so-q">Поиск</label>
            <input id="so-q" value={search} placeholder="номер заказа, номер счёта, № сделки"
                   onChange={(e) => resetAnd(setSearch)(e.target.value)} />
          </div>
          <div className="f" style={{ minWidth: 180 }}>
            <label htmlFor="so-st">Статус в 1С</label>
            <select id="so-st" value={status} onChange={(e) => resetAnd(setStatus)(e.target.value)}>
              <option value="">любой</option>
              {statuses.map((s) => <option key={s} value={s}>{supplierStatusRu(s)}</option>)}
            </select>
          </div>
          <div className="f" style={{ minWidth: 200 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 18 }}>
              <input type="checkbox" checked={onlyOpen} style={{ width: 'auto' }}
                     onChange={(e) => resetAnd(setOnlyOpen)(e.target.checked)} />
              только не поступившие
            </label>
          </div>
        </FilterBar>
        <div className="hint" style={{ marginTop: 10 }}>
          Документ «Заказ поставщику» из 1С УТ. Своего поля сделки у него нет — номер сделки
          закупщик пишет в комментарий свободным текстом («Сд. 104138», «под сделку 93035»),
          оттуда мы его и читаем. Не распознали — колонка пустая, выдумывать сделку нельзя.
          {' '}Выборка здесь по всей компании, а не по вашей зоне: OData 1С не знает про
          ответственного в Bitrix, и сузить её по человеку ядру нечем — в отличие от вкладки
          с заявками, где сужение по зоне работает и помечается.
        </div>
      </div>

      <OdataState isLoading={docs.isLoading || dicts.isLoading} error={docs.error ?? dicts.error}>
        <div style={{ marginBottom: 12 }}>
          <OdataFreshness meta={docs.data?.meta} fetching={docs.isFetching}
                          onRefresh={() => void docs.refetch()} />
        </div>

        {docs.data?.truncated && (
          <div className="notice" style={{ marginBottom: 12 }}>
            В окне {docs.data.inWindow} заказов, загружены первые {docs.data.rows.length}.
            Сузьте окно, чтобы увидеть остальные — целиком таблицу заказов 1С не отдаёт
            (на портале 55 такой запрос рвал соединение).
          </div>
        )}
        {dicts.data?.failed.length ? (
          <div className="notice" style={{ marginBottom: 12 }}>
            Справочники 1С не пришли: {dicts.data.failed.join(', ')}. В соответствующих
            колонках будет «—» — это не «пусто в 1С», а «мы не смогли прочитать».
          </div>
        ) : null}

        <div className="card" style={{ marginBottom: 16 }}>
          <div className="tiles">
            <div className="tile">
              <div className="t-label">Заказов в выборке</div>
              <div className="t-sum">{totals.count}</div>
              <div className="t-label">из {docs.data?.inWindow ?? 0} в окне</div>
            </div>
            <div className="tile">
              <div className="t-label">Сумма заказов</div>
              <div className="t-sum">{fmtMoney(totals.sum)}</div>
              <div className="t-label">по выборке</div>
            </div>
            <div className="tile">
              <div className="t-label">Уже поступило</div>
              <div className="t-sum">{fmtMoney(arrived.sum)}</div>
              <div className="t-label">{arrived.count} заказов</div>
            </div>
            <div className={`tile${totals.count - arrived.count ? ' hot' : ''}`}>
              <div className="t-label">Ждём поступления</div>
              <div className="t-sum">{totals.count - arrived.count}</div>
              <div className="t-label">заказов без даты прихода</div>
            </div>
          </div>
        </div>

        <div className="card">
          {shown.length === 0 ? (
            <div className="empty">
              <div className="empty-title">Заказов поставщику под эти условия нет</div>
              <div className="empty-hint">Расширьте окно или снимите фильтры.</div>
            </div>
          ) : (
            <div className="table-wrap cards">
              <table>
                <thead>
                  <tr>
                    <th className="nowrap">№ заказа</th>
                    <th className="nowrap">Дата</th>
                    <th>Поставщик</th>
                    <th className="num">Сумма</th>
                    <th className="nowrap">Статус</th>
                    <th className="nowrap">№ счёта поставщика</th>
                    <th className="nowrap">Поступил</th>
                    <th className="nowrap">Ждали к</th>
                    <th>Склад</th>
                    <th className="nowrap">Сделка</th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map((r) => (
                    <tr key={r.ref}>
                      <td className="mono nowrap" data-label="№ заказа">{r.number}</td>
                      <td className="nowrap" data-label="Дата">{r.day ? fmtDate(r.day) : <span className="muted">—</span>}</td>
                      <td data-label="Поставщик" title={r.comment || undefined}>
                        {pageNames.data?.partner[r.supplierRef] || <span className="muted">—</span>}
                      </td>
                      <td className="num" data-label="Сумма">{fmtMoney(r.sum)}</td>
                      <td className="nowrap" data-label="Статус">
                        <span className={`badge ${r.arrivedDay ? 'won' : 'work'}`}>{r.status || '—'}</span>
                        {!r.posted && (
                          <div><span className="badge lose">не проведён</span></div>
                        )}
                      </td>
                      <td className="mono nowrap" data-label="№ счёта поставщика">{r.supplierInvoiceNo || <span className="muted">—</span>}</td>
                      <td className="nowrap" data-label="Поступил">
                        {r.arrivedDay ? fmtDate(r.arrivedDay) : <span className="muted">нет</span>}
                      </td>
                      <td className="nowrap" data-label="Ждали к">
                        {r.wantedDay ? fmtDate(r.wantedDay) : <span className="muted">—</span>}
                      </td>
                      <td data-label="Склад">{fmtText(r.warehouse)}</td>
                      <td className="nowrap" data-label="Сделка"><EntityLink kind="deal" id={r.dealId} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {filtered.length > SUPPLY_PAGE && (
            <div className="pager">
              <button disabled={page === 0} onClick={() => setPage(Math.max(0, page - 1))}>
                ← назад
              </button>
              <span className="muted">
                {page * SUPPLY_PAGE + 1}–{Math.min((page + 1) * SUPPLY_PAGE, filtered.length)}
                {' из '}{filtered.length}
              </span>
              <button disabled={(page + 1) * SUPPLY_PAGE >= filtered.length}
                      onClick={() => setPage(page + 1)}>
                вперёд →
              </button>
            </div>
          )}
        </div>
      </OdataState>
    </>
  )
}

// ═══════════════════════════ чего здесь нет и почему ═══════════════════════════

function NotHere() {
  return (
    <div className="card">
      <h2>Чего здесь нет и почему</h2>
      <dl className="dl">
        <dt>Заявки ЗКП (ЗКП-00047 и далее)</dt>
        <dd>
          Заявка закупщика на портале 55 целиком лежит в хранилище самого портала
          (Cloudflare KV, ключ <span className="mono">zkp:all</span>): отдел, статья, склад,
          позиции, срок, «взял в работу». Ни в Bitrix, ни в 1С её нет, поэтому ядро её не
          отдаёт и отдать не может. В CRM видна вторая половина той же цепочки — счёт,
          который по заявке пошёл на согласование.
        </dd>

        <dt>Файл счёта и ход согласования</dt>
        <dd>
          Сам PDF и цепочка заданий бизнес-процесса живут в Bitrix. В карточке заявки есть
          прямая ссылка на элемент списка 18 — там и файл, и кто на каком этапе согласовал.
          Ядро файлы не отдаёт: у него зеркало полей, а не файловое хранилище.
        </dd>

        <dt>Список 18 «Счета на оплату»</dt>
        <dd>
          Ядро его отдаёт целиком, синк живой. Раздел на него намеренно не
          опирается: в ответе <span className="mono">lists.element.get</span> нет
          <span className="mono"> TIMESTAMP_X</span>, значения свойств приходят картами
          «id значения → значение», а карточка {PUR_TYPE_ID} несёт те же поля уже
          разобранными и с человеческими названиями из справочника ядра.
        </dd>

        <dt>Распознавание счёта, отправка на согласование, выгрузка в 1С</dt>
        <dd>
          Это запись: элемент списка 18 создаётся от имени сотрудника и запускает
          бизнес-процесс, а выгрузка создаёт заказ поставщику в 1С. Отменить ни то, ни другое
          нельзя. Такие кнопки появятся отдельным шагом и с согласия владельца процесса —
          пока они остаются на портале 55.
        </dd>

        <dt>Связь заявки с заказом в 1С</dt>
        <dd>
          Единственная машинная связка — поле «Номер заказа поставщику в 1С» в карточке
          заявки; на живых данных оно заполнено у единиц карточек. Поэтому вкладки
          не сшиты в одну таблицу: сопоставлять их приходится глазами, по номеру счёта
          поставщика.
        </dd>
      </dl>
    </div>
  )
}
